import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import LocationPopup from '../components/LocationPopup';
import Sidebar from '../components/Sidebar';
// import CityChat from '../components/CityChat'; // Chat disabled
import SuggestLocationModal from '../components/SuggestLocationModal'; // Import the new modal
import { FaPlus, FaBars } from 'react-icons/fa'; // Import FaPlus
import L from 'leaflet';
import { useAuth } from '../context/AuthContext';
import { MapInteractionContext } from '../components/Sidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { suggestLocation } from '../services/locationService'; // Import the new service function

// CSS imports
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '../styles/CityMap.css';

// Import SVG files directly
import culturalIcon from '../assets/icons/cultural.svg';
import documentsIcon from '../assets/icons/documents.svg';
import educationalIcon from '../assets/icons/educational.svg';
import recreationIcon from '../assets/icons/recreation.svg';
import restaurantIcon from '../assets/icons/restaurant.svg';
import shoppingIcon from '../assets/icons/shopping.svg';

const DEFAULT_CENTER = {
    warsaw: [52.237049, 21.017532],
    kuopio: [62.892477, 27.678892],
    porto: [41.157944, -8.629105]
};

// Category colors for icon backgrounds
const CATEGORY_COLORS = {
    cultural: '#ddacf5', // Purple
    documents: '#0081a7', // Blue
    educational: '#ffcb00', // Green
    recreation: '#f8aa4b', // Orange
    restaurants: '#ff6150', // Red
    shops: '#75E6DA' // Teal
};

// Custom icon style - update popupAnchor
const customIconStyle = {
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -650],
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null,
    className: 'custom-icon'
};

// Function to create custom icon for SVG with colored background and important badge
const createCustomIcon = (iconUrl, category, isImportant = false) => {
    const categoryColor = CATEGORY_COLORS[category] || '#7F8C8D'; // Default gray if category not found

    // Create the HTML for the icon with colored background
    let iconHtml = `
        <div class="marker-circle" style="background-color: ${categoryColor}">
            <img src="${iconUrl}" class="marker-icon" />
            ${isImportant ? '<div class="important-marker-badge"></div>' : ''}
        </div>
    `;

    // Add pulse effect for important locations
    if (isImportant) {
        iconHtml += `<div class="marker-pulse" style="border-color: ${categoryColor}"></div>`;
    }

    return L.divIcon({
        ...customIconStyle,
        html: iconHtml,
        className: `custom-icon-svg ${isImportant ? 'important-marker' : ''}`
    });
};

// Create a context for managing popup state
const PopupContext = React.createContext();

// Provider component for popup state management
const PopupProvider = ({ children, activePopup, setActivePopup }) => {
    const { setIsPopupOpen } = React.useContext(MapInteractionContext) || {};

    useEffect(() => {
        if (setIsPopupOpen) {
            setIsPopupOpen(activePopup !== null);
        }
    }, [activePopup, setIsPopupOpen]);

    return (
        <PopupContext.Provider value={{ activePopup, setActivePopup }}>
            {children}
        </PopupContext.Provider>
    );
};

// Custom marker component - updated for array coordinates
const MarkerComponent = ({ location, icon, cityName }) => {
    const { setActivePopup } = React.useContext(PopupContext);
    const markerRef = useRef(null);

    // Check if coordinates exist and are in the correct format
    if (!location.coordinates || !Array.isArray(location.coordinates) || location.coordinates.length !== 2) {
        console.error("Invalid coordinates format for location:", location.name);
        return null;
    }

    // Ensure boolean values for important flag
    const isImportant = location.isImportant === true;

    // Use array coordinates directly
    return (
        <Marker
            position={[location.coordinates[0], location.coordinates[1]]}
            icon={icon}
            ref={markerRef}
            eventHandlers={{
                click: () => {
                    // Clone the location object to avoid reference issues
                    const locationData = {
                        ...location,
                        isImportant: isImportant
                    };

                    setActivePopup({
                        location: locationData,
                        markerRef,
                        cityName
                    });
                }
            }}
        />
    );
};

// Custom popup container component
const CustomPopupContainer = () => {
    const { activePopup, setActivePopup } = React.useContext(PopupContext);
    const map = useMap();
    const popupRef = useRef(null);
    const [isReviewsOpen, setIsReviewsOpen] = useState(false);

    const handleReviewsToggle = (isOpen) => {
        setIsReviewsOpen(isOpen);
        if (activePopup && popupRef.current) {
            setTimeout(positionPopup, 10);
        }
    };

    // Define positionPopup function outside the effect for reuse
    const positionPopup = useCallback(() => {
        if (!activePopup || !popupRef.current) return;

        const marker = activePopup.markerRef.current;
        if (!marker) return;

        const markerLatLng = marker.getLatLng();
        const markerPoint = map.latLngToContainerPoint(markerLatLng);
        const popup = popupRef.current;

        // Use CSS transforms for positioning. Let CSS handle the pixel-perfect alignment.
        // We just provide the anchor point.
        popup.style.bottom = `calc(100% - ${markerPoint.y}px + 30px)`; // Position above marker icon
        popup.style.left = `${markerPoint.x}px`;

        // The new CSS will use transform: translate(-50%, -100%) to center itself.
        // We just need to make it visible.
        popup.classList.add('visible');
    }, [activePopup, map]);

    useEffect(() => {
        if (!activePopup || !popupRef.current) {
            return;
        }

        const marker = activePopup.markerRef.current;
        if (!marker) return;

        const markerLatLng = marker.getLatLng();

        // Center map on marker with offset for popup visibility
        const mapHeight = map.getContainer().clientHeight;
        const targetPoint = map.project(markerLatLng).subtract([0, mapHeight * 0.3]);
        const targetLatLng = map.unproject(targetPoint);

        // Use panTo for a smoother, simpler animation without zoom changes
        map.panTo(targetLatLng, {
            animate: true,
            duration: 0.75, // A smooth but quick pan
        });

        // Use a slight delay to ensure the map has started panning before positioning
        setTimeout(() => positionPopup(), 50);

        const handleMapMove = () => {
            positionPopup();
        };

        const handleMapClick = (e) => {
            // If the click is on the marker, don't close the popup
            if (e.originalEvent.target.closest('.leaflet-marker-icon')) {
                return;
            }
            // If the click is inside the popup container, don't close it
            if (popupRef.current && popupRef.current.contains(e.originalEvent.target)) {
                return;
            }
            setActivePopup(null);
        };

        const handleWheel = (e) => {
            e.stopPropagation(); // Stop map zoom when scrolling inside popup
        };

        const popupEl = popupRef.current;
        if (popupEl) {
            popupEl.addEventListener('wheel', handleWheel);
        }

        map.on('move', handleMapMove);
        map.on('click', handleMapClick);

        return () => {
            map.off('move', handleMapMove);
            map.off('click', handleMapClick);
            if (popupEl) {
                popupEl.removeEventListener('wheel', handleWheel);
            }
        };
    }, [activePopup, map, isReviewsOpen, positionPopup, setActivePopup]); // Rerun when activePopup changes or reviews open/close

    const handleClose = () => {
        setActivePopup(null);
    };

    if (!activePopup) return null;

    return (
        <div
            ref={popupRef}
            className={`map-popup-container ${isReviewsOpen ? 'with-reviews' : ''}`}
        >
            <LocationPopup
                {...activePopup.location}
                locationId={activePopup.location.id}
                locationName={activePopup.location.name}
                locationDescription={activePopup.location.description}
                locationAddress={activePopup.location.address}
                locationTips={activePopup.location.tips}
                locationHours={activePopup.location.openingHours}
                locationMoreInfo={activePopup.location.moreInfo}
                isImportant={activePopup.location.isImportant}
                cityName={activePopup.cityName}
                onClose={handleClose}
                onReviewsToggle={handleReviewsToggle}
            />
            <div className="map-popup-arrow"></div>
        </div>
    );
};

const CityMap = () => {
    const { cityName, locationId } = useParams();
    const navigate = useNavigate();
    const { search } = useLocation(); // Use standard useLocation hook
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [center, setCenter] = useState(DEFAULT_CENTER[cityName] || DEFAULT_CENTER.warsaw);
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [activePopup, setActivePopup] = useState(null); // Lifted state
    const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false); // State for the new modal
    const [suggestionStatus, setSuggestionStatus] = useState({ message: '', type: '' }); // For success/error messages

    const { currentUser, idToken: token } = useAuth();

    const [selectedFilters, setSelectedFilters] = useState({
        documents: true,
        shops: true,
        restaurants: true,
        recreation: true,
        cultural: true,
        educational: true,
        important: true
    });

    const mapRef = useRef(null);
    const cityRef = useRef(cityName);

    const showLocationPopup = useCallback((locationId) => {
        const location = locations.find(loc => loc.id === locationId);
        if (location) {
            setActivePopup({
                location: location,
                markerRef: { current: { getLatLng: () => L.latLng(location.coordinates) } },
                cityName
            });
        } else if (!location) {
            console.error("Location not found:", locationId);
        }
    }, [locations, cityName, setActivePopup]);

    // This effect handles URL query parameters for special actions
    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const shouldOpenChat = queryParams.get('chat') === 'open';

        // if (shouldOpenChat) { // Chat disabled
        //     setIsChatOpen(true);
        //     // Use a timeout to ensure the state update is processed before cleaning the URL
        //     setTimeout(() => {
        //         navigate(`/city/${cityName}`, { replace: true });
        //     }, 100);
        // }

        // New logic to handle the `goto` parameter
        const locationIdToGoto = queryParams.get('goto');
        if (locationIdToGoto && locations.length > 0) {
            // Ensure locations are loaded before trying to show the popup
            setTimeout(() => {
                showLocationPopup(locationIdToGoto);
                navigate(`/city/${cityName}`, { replace: true });
            }, 100);
        }
    }, [search, navigate, cityName, locations, showLocationPopup]);

    // Effect to invalidate map size when sidebar toggles
    useEffect(() => {
        if (mapRef.current) {
            // Use a short timeout to ensure the CSS transition has started
            setTimeout(() => {
                mapRef.current.invalidateSize();
            }, 300); // 300ms should be enough for the sidebar animation
        }
    }, [isSidebarOpen]);

    // Effect to handle window resize for sidebar visibility
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Effect to handle city change and data fetching
    useEffect(() => {
        const fetchLocations = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/locations/${cityName}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch locations: ${response.status} ${response.statusText}`);
                }

                const data = await response.json();

                const fetchedLocations = data.locations.map(location => ({
                    ...location,
                    id: location.id,
                    coordinates: location.coordinates
                }));

                setLocations(fetchedLocations);
                setCenter(fetchedLocations[0]?.coordinates || DEFAULT_CENTER[cityName] || DEFAULT_CENTER.warsaw);
                cityRef.current = cityName;

            } catch (error) {
                console.error("Error fetching locations:", error);
                setLocations([]);
            } finally {
                setLoading(false);
            }
        };

        fetchLocations();

    }, [cityName, token]);

    const handleFilterChange = (name, checked) => {
        setSelectedFilters(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const mapInteractionValue = useMemo(() => ({
        showLocationPopup,
        isPopupOpen,
        setIsPopupOpen
    }), [showLocationPopup, isPopupOpen]);


    useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current;
            if (isPopupOpen) {
                map.doubleClickZoom.disable();
            } else {
                map.doubleClickZoom.enable();
            }
        }
    }, [isPopupOpen]);


    const filteredLocations = Object.values(selectedFilters).every(v => !v)
        ? locations
        : locations.filter(location => {
            const categoryMatch = selectedFilters[location.category];
            const importantMatch = location.isImportant ? selectedFilters.important : true;
            return categoryMatch && importantMatch;
        });

    // Handler for the suggestion modal
    const handleOpenSuggestionModal = () => {
        if (activePopup) {
            setActivePopup(null); // Close any open popup
        }
        if (!currentUser) {
            alert('You must be logged in to suggest a location.');
            return;
        }
        if (!currentUser.is_verified_student) {
            alert('You must be a verified student to suggest a location.');
            return;
        }
        setIsSuggestionModalOpen(true);
    };

    const handleCloseSuggestionModal = () => {
        setIsSuggestionModalOpen(false);
    };

    const handleSubmitSuggestion = async (city, locationData) => {
        setSuggestionStatus({ message: 'Submitting...', type: 'loading' });
        try {
            const response = await suggestLocation(token, city, locationData);
            setSuggestionStatus({ message: response.message, type: 'success' });
            // Close the modal after a short delay to show the success message
            setTimeout(() => {
                handleCloseSuggestionModal();
                setSuggestionStatus({ message: '', type: '' }); // Reset status
            }, 2000);
        } catch (error) {
            setSuggestionStatus({ message: `Error: ${error.message}`, type: 'error' });
        }
    };

    const getIcon = (category, isImportant = false) => {
        const icons = {
            cultural: culturalIcon,
            documents: documentsIcon,
            educational: educationalIcon,
            recreation: recreationIcon,
            restaurants: restaurantIcon,
            shops: shoppingIcon,
        };
        return createCustomIcon(icons[category], category, isImportant);
    };

    const handleChatToggle = (isOpen) => {
        setIsChatOpen(isOpen);
    };

    const createClusterCustomIcon = function (cluster) {
        const childCount = cluster.getChildCount();
        return L.divIcon({
            html: `<div class="cluster-marker">${childCount}</div>`,
            className: 'custom-cluster-icon',
            iconSize: [40, 40]
        });
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <PopupProvider activePopup={activePopup} setActivePopup={setActivePopup}>
            <MapInteractionContext.Provider value={mapInteractionValue}>
                <div className="map-page">
                    <button
                        className={`sidebar-toggle-button ${isSidebarOpen ? 'hidden' : ''}`}
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    >
                        <FaBars />
                    </button>
                    <Sidebar
                        cityName={cityName}
                        onFilterChange={handleFilterChange}
                        selectedFilters={selectedFilters}
                        isOpen={isSidebarOpen}
                        onClose={() => setIsSidebarOpen(false)}
                    />
                    <div className={`map-container-wrapper ${isSidebarOpen ? 'sidebar-open' : ''}`}>
                        <MapContainer
                            center={center}
                            zoom={13}
                            style={{ height: '100%', width: '100%' }}
                            zoomControl={false}
                            doubleClickZoom={!isPopupOpen}
                            ref={mapRef}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MarkerClusterGroup
                                iconCreateFunction={createClusterCustomIcon}
                                spiderfyOnMaxZoom={false}
                                showCoverageOnHover={false}
                                disableClusteringAtZoom={17}
                                maxClusterRadius={60}
                                zoomToBoundsOnClick={true}
                                animate={true}
                                removeOutsideVisibleBounds={true}
                                animateAddingMarkers={true}
                            >
                                {filteredLocations.map((location, index) => (
                                    <MarkerComponent
                                        key={`${location.id}-${index}`}
                                        location={location}
                                        icon={getIcon(location.category, location.isImportant)}
                                        cityName={cityName}
                                    />
                                ))}
                            </MarkerClusterGroup>

                            {locationId && (
                                <SelectedLocationHandler
                                    locationId={locationId}
                                    locations={locations}
                                    cityName={cityName}
                                    onDone={() => navigate(`/city/${cityName}`)}
                                />
                            )}

                            <CustomPopupContainer />
                        </MapContainer>
                        {/* <CityChat // Chat disabled
                            cityName={cityName}
                            isOpen={isChatOpen}
                            onToggle={handleChatToggle}
                            /> */}
                        {/* Add the FAB for suggesting a location */}
                        <button className="suggest-location-fab" onClick={handleOpenSuggestionModal} title="Suggest a New Location">
                            <FaPlus />
                        </button>
                    </div>
                </div>
                {/* Render the suggestion modal */}
                <SuggestLocationModal
                    isOpen={isSuggestionModalOpen}
                    onClose={handleCloseSuggestionModal}
                    onSubmit={handleSubmitSuggestion}
                    city={cityName}
                    status={suggestionStatus}
                />
            </MapInteractionContext.Provider>
        </PopupProvider>
    );
};


const SelectedLocationHandler = ({ locationId, locations, cityName, onDone }) => {
    const map = useMap();
    const { showLocationPopup } = React.useContext(MapInteractionContext);

    useEffect(() => {
        if (locationId && locations.length > 0) {
            // Need to give the map a moment to be ready
            setTimeout(() => {
                const location = locations.find(loc => loc.id === locationId);
                if (location) {
                    showLocationPopup(locationId);

                    const targetPoint = map.project(location.coordinates).subtract([0, map.getSize().y * 0.3]);
                    const targetLatLng = map.unproject(targetPoint);

                    map.flyTo(targetLatLng, 15, { // Zoom in a bit more for detail
                        duration: 1.5,
                        easeLinearity: 0.5
                    });
                }
            }, 100); // Small delay
            onDone(); // Call onDone to remove the locationId from the URL
        }
    }, [locationId, locations, showLocationPopup, onDone, map]);

    return null;
};

export default CityMap;